import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MapView from '../../components/MapView';
import { useRideStore } from '../../store/rideStore';
import { useDriverStore } from '../../store/driverStore';
import { wsService } from '../../services/websocket';
import { locationUpdateService } from '../../services/locationUpdateService';
import { DRIVER_EVENTS } from '../../constants/websocket-events';
import { normalizeRideStatus, isCompletedRide, RideStatus } from '../../constants/ride-status';
import { useAlertManager } from '../../components/AlertProvider';
import { getCurrentPosition } from '../../utils/geolocation';
import { calculateDistance, formatDistance, calculateETA, formatETA } from '../../utils/distance';
import { getRouteDirections, RoutePoint } from '../../utils/directions';
import { calculateBounds, boundsCenter, estimateZoom } from '../../utils/mapUtils';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import './ActiveRideScreen.css';

const ActiveRideScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentRide, completeRide, updateCurrentRide, getDriverActiveRide } = useRideStore();
  const { getVerificationStatus } = useDriverStore();
  const { showAlert } = useAlertManager();
  const [riderLocation, setRiderLocation] = useState<any>(null);
  const [driverLocation, setDriverLocation] = useState<any>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [eta, setEta] = useState<number | null>(null);
  const [routePath, setRoutePath] = useState<RoutePoint[]>([]);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const locationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousStatusRef = useRef<string | null>(null);
  const hasCheckedActiveRideRef = useRef<boolean>(false);
  const currentRideRef = useRef(currentRide);


  useEffect(() => {
    currentRideRef.current = currentRide;
  }, [currentRide]);

  const normalizedStatus = useMemo(() => {
    if (!currentRide) return RideStatus.PENDING;
    return normalizeRideStatus(currentRide.status);
  }, [currentRide?.status]);

  const isStarted = useMemo(() => normalizedStatus === RideStatus.STARTED, [normalizedStatus]);

  const getStatusColor = (status: string) => {
    const normalized = normalizeRideStatus(status);
    switch (normalized) {
      case RideStatus.DRIVER_ARRIVED:
        return Colors.info;
      case RideStatus.STARTED:
        return Colors.success;
      case RideStatus.COMPLETED:
        return Colors.success;
      default:
        return Colors.primary;
    }
  };


  useEffect(() => {
    const handleRideUpdate = (data: any) => {
      const rideData = data.ride || data;
      const currentRideValue = currentRideRef.current;
      const newStatus = normalizeRideStatus(rideData?.status || data?.status || currentRideValue?.status);
      const previousStatus = previousStatusRef.current || normalizeRideStatus(currentRideValue?.status);


      if (currentRideValue && currentRideValue.id) {
        const rideId = currentRideValue.id.toString();

        if (rideData && rideData.id && rideData.id.toString() === rideId) {
          const updatedRide = {
            ...currentRideValue,
            ...rideData,
            status: rideData.status || rideData.rideStatusConfig?.name || currentRideValue.status,
          };
          updateCurrentRide(updatedRide);
        } else if (data && data.id && data.id.toString() === rideId) {
          const updatedRide = {
            ...currentRideValue,
            ...data,
            status: data.status || data.rideStatusConfig?.name || currentRideValue.status,
          };
          updateCurrentRide(updatedRide);
        } else if (rideData && rideData.id) {

          if (rideData.id.toString() === rideId) {
            const updatedRide = {
              ...currentRideValue,
              ...rideData,
              status: rideData.status || rideData.rideStatusConfig?.name || currentRideValue.status,
            };
            updateCurrentRide(updatedRide);
          }
        }
      } else if (rideData && rideData.id) {


        const transformedRide = {
          ...rideData,
          status: rideData.status || rideData.rideStatusConfig?.name || rideData.status,
        };
        updateCurrentRide(transformedRide);
      }

      if (newStatus !== previousStatus && previousStatusRef.current && newStatus !== RideStatus.COMPLETED) {
        const statusMessages: Record<string, string> = {
          [RideStatus.DRIVER_ASSIGNED]: 'You have been assigned to this ride!',
          [RideStatus.DRIVER_ARRIVED]: 'You have arrived at the pickup location!',
          [RideStatus.STARTED]: 'Ride has started! Navigate to the destination.',
          [RideStatus.CANCELLED]: 'Ride has been cancelled.',
        };

        const message = statusMessages[newStatus];
        if (message) {
          showAlert('Ride Status Updated', message, [{ text: 'OK' }]);
        }
      }

      previousStatusRef.current = newStatus;


      if (newStatus === RideStatus.CANCELLED) {
        updateCurrentRide(null);
        setTimeout(() => {
          navigate('/');
        }, 2000); 
      } else if (isCompletedRide(newStatus) && newStatus === RideStatus.COMPLETED) {
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    };

    const handleLocationUpdate = (data: any) => {
      setRiderLocation(data);
    };

    wsService.on(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.RIDE_STARTED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.RIDE_ARRIVED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.RIDE_DRIVER_ASSIGNED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.RIDE_COMPLETED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.RIDE_CANCELLED, handleRideUpdate);
    wsService.on(DRIVER_EVENTS.LOCATION_UPDATED, handleLocationUpdate);

    return () => {
      wsService.off(DRIVER_EVENTS.RIDE_UPDATED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.RIDE_STARTED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.RIDE_ARRIVED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.RIDE_DRIVER_ASSIGNED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.RIDE_COMPLETED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.RIDE_CANCELLED, handleRideUpdate);
      wsService.off(DRIVER_EVENTS.LOCATION_UPDATED, handleLocationUpdate);
    };
  }, []); 

  useEffect(() => {


    if (!currentRide && !hasCheckedActiveRideRef.current) {
      hasCheckedActiveRideRef.current = true;


      const checkActiveRide = async () => {
        try {
          const activeRide = await getDriverActiveRide();
          if (activeRide && activeRide.id) {

            updateCurrentRide(activeRide);
            hasCheckedActiveRideRef.current = false; 
          } else {


            updateCurrentRide(null);

            setTimeout(() => {

              if (!currentRideRef.current) {
                navigate('/');
              }
            }, 500);
          }
        } catch (error) {

          console.error('Failed to check active ride:', error);
          updateCurrentRide(null);
          setTimeout(() => {
            if (!currentRideRef.current) {
              navigate('/');
            }
          }, 500);
        }
      };



      const timeoutId = setTimeout(checkActiveRide, 300);
      return () => clearTimeout(timeoutId);
    } else if (currentRide) {

      hasCheckedActiveRideRef.current = false;
    }


    if (!currentRide) {
      return;
    }

    const normalizedStatus = normalizeRideStatus(currentRide.status);
    const isStarted = normalizedStatus === RideStatus.STARTED;

    const updateInterval = isStarted ? 2000 : 5000;

    const updateDriverLocation = () => {
      getCurrentPosition(
        (position: any) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setDriverLocation(loc);


          if (isStarted && currentRide && currentRide.id) {
            locationUpdateService.sendLocationUpdate(
              loc.lat,
              loc.lng,
              position.coords.heading || undefined,
              position.coords.speed || undefined,
              currentRide.id.toString(),
            );
          }
        },
        (error: any) => {
          console.warn('Location error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    };

    updateDriverLocation();
    locationIntervalRef.current = setInterval(updateDriverLocation, updateInterval);


    if (isStarted && currentRide && currentRide.id) {
      locationUpdateService.startTracking(currentRide.id.toString());
    } else {
      locationUpdateService.stopTracking();
    }

    if (!previousStatusRef.current && currentRide) {
      previousStatusRef.current = normalizeRideStatus(currentRide.status);
    }

    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
      locationUpdateService.stopTracking();
    };
  }, [currentRide, navigate, updateCurrentRide, getDriverActiveRide]);


  useEffect(() => {
    if (!currentRide) {
      setDistance(null);
      setEta(null);
      setRoutePath([]);
      return;
    }

    const normalizedStatus = normalizeRideStatus(currentRide.status);
    if (normalizedStatus !== RideStatus.STARTED) {
      setDistance(null);
      setEta(null);
      setRoutePath([]);
      return;
    }


    if (!driverLocation || !driverLocation.lat || !driverLocation.lng) {
      console.log('⚠️ Driver location not available for route calculation');
      return;
    }



    const calculatedEndPoint = endPoint;

    if (!calculatedEndPoint.latitude || !calculatedEndPoint.longitude || 
        calculatedEndPoint.latitude === 0 || calculatedEndPoint.longitude === 0 ||
        isNaN(calculatedEndPoint.latitude) || isNaN(calculatedEndPoint.longitude)) {
      console.warn('⚠️ End point not available for route calculation', {
        calculatedEndPoint,
        currentRideEndPoint: currentRide.end_point,
        locations: currentRide.locations,
        rideId: currentRide.id,
        destinationAddress: currentRide.destination_address,
      });
      return;
    }

    console.log('🔄 Calculating route:', {
      from: { lat: driverLocation.lat, lng: driverLocation.lng },
      to: { lat: calculatedEndPoint.latitude, lng: calculatedEndPoint.longitude },
    });


    const calculateRoute = async () => {

      const dist = calculateDistance(
        driverLocation.lat,
        driverLocation.lng,
        calculatedEndPoint.latitude,
        calculatedEndPoint.longitude,
      );
      const estimatedEta = calculateETA(dist);


      setDistance(dist);
      setEta(estimatedEta);
      console.log('📏 Initial distance/ETA (fallback):', { distance: dist, eta: estimatedEta });


      try {
        if (window.google && window.google.maps && window.google.maps.DirectionsService) {
          const route = await getRouteDirections(
            { latitude: driverLocation.lat, longitude: driverLocation.lng },
            { latitude: calculatedEndPoint.latitude, longitude: calculatedEndPoint.longitude }
          );

          if (route && route.path && route.path.length > 0) {
            console.log('✅ Route calculated successfully:', {
              distance: route.distance,
              duration: route.duration,
              pathLength: route.path.length,
            });
            setRoutePath(route.path);
            setDistance(route.distance);
            setEta(route.duration);
          } else {

            console.log('⚠️ Route calculation returned null, using fallback');
            setRoutePath([]);
          }
        } else {
          console.log('⚠️ Google Maps API not loaded, using fallback');
          setRoutePath([]);
        }
      } catch (error) {
        console.warn('⚠️ Route calculation failed, using fallback:', error);
        setRoutePath([]);
      }
    };

    calculateRoute();
  }, [driverLocation, currentRide, isStarted]);

  const handleComplete = async () => {
    if (!currentRide) return;

    const normalizedStatus = normalizeRideStatus(currentRide.status);

    if (normalizedStatus !== RideStatus.STARTED) {
      showAlert('Error', `Ride cannot be completed. Current status: ${normalizedStatus}`, [{ text: 'OK' }]);
      return;
    }

    try {
      const rideId = currentRide.id;



      let finalFare: number = 0;

      if (currentRide.ride_fare && typeof currentRide.ride_fare === 'number' && currentRide.ride_fare > 0) {
        finalFare = currentRide.ride_fare;
      } else if (currentRide.estimated_fare && typeof currentRide.estimated_fare === 'number' && currentRide.estimated_fare > 0) {
        finalFare = currentRide.estimated_fare;
      } else {

        showAlert('Error', 'Ride fare is not available. Cannot complete ride.', [{ text: 'OK' }]);
        return;
      }


      finalFare = Number(finalFare);
      if (isNaN(finalFare) || finalFare <= 0) {
        showAlert('Error', 'Invalid ride fare. Cannot complete ride.', [{ text: 'OK' }]);
        return;
      }

      await completeRide(rideId, finalFare);


      try {
        await getVerificationStatus();
      } catch (error) {
        console.warn('Failed to refresh driver status:', error);
      }

      showAlert('Success', 'Ride completed successfully!', [
        {
          text: 'OK',
          onPress: () => {
            setTimeout(() => {
              navigate('/');
            }, 500);
          },
        },
      ]);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to complete ride';
      showAlert('Error', errorMessage, [{ text: 'OK' }]);
    }
  };



  const startPoint = useMemo(() => {
    if (!currentRide) {
      return { latitude: 0, longitude: 0 };
    }
    return currentRide.start_point || {
      latitude: currentRide.locations?.[0]?.latitude || 0,
      longitude: currentRide.locations?.[0]?.longitude || 0,
    };
  }, [currentRide?.start_point, currentRide?.locations]);

  const endPoint = useMemo(() => {
    if (!currentRide) {
      return { latitude: 0, longitude: 0 };
    }

    const endPointData = currentRide.end_point || {
      latitude: currentRide.locations?.[currentRide.locations?.length - 1]?.latitude || 
                (currentRide as any).destination_latitude || 
                (currentRide as any).destinationLatitude || 0,
      longitude: currentRide.locations?.[currentRide.locations?.length - 1]?.longitude || 
                 (currentRide as any).destination_longitude || 
                 (currentRide as any).destinationLongitude || 0,
      address: currentRide.destination_address || 
               currentRide.locations?.[currentRide.locations?.length - 1]?.address,
    };


    if ((!endPointData.latitude || endPointData.latitude === 0) && currentRide.destination_address) {
      console.warn('⚠️ End point coordinates are 0 but address exists:', {
        address: currentRide.destination_address,
        endPoint: currentRide.end_point,
        locations: currentRide.locations,
      });
    }

    return endPointData;
  }, [currentRide?.end_point, currentRide?.locations, currentRide?.destination_address]);


  const mapCenter = useMemo(() => {
    const points: Array<{ lat: number; lng: number }> = [];

    if (driverLocation && driverLocation.lat && driverLocation.lng) {
      points.push({ lat: driverLocation.lat, lng: driverLocation.lng });
    }

    if (startPoint.latitude && startPoint.longitude) {
      points.push({ lat: startPoint.latitude, lng: startPoint.longitude });
    }

    if (endPoint.latitude && endPoint.longitude) {
      points.push({ lat: endPoint.latitude, lng: endPoint.longitude });
    }

    if (points.length === 0) {

      return null;
    }

    if (points.length === 1) {
      return points[0];
    }

    const bounds = calculateBounds(points);
    if (!bounds) {
      return null;
    }
    return boundsCenter(bounds);
  }, [driverLocation, startPoint, endPoint]);

  const mapZoom = useMemo(() => {
    const points: Array<{ lat: number; lng: number }> = [];

    if (driverLocation && driverLocation.lat && driverLocation.lng) {
      points.push({ lat: driverLocation.lat, lng: driverLocation.lng });
    }

    if (startPoint.latitude && startPoint.longitude) {
      points.push({ lat: startPoint.latitude, lng: startPoint.longitude });
    }

    if (endPoint.latitude && endPoint.longitude) {
      points.push({ lat: endPoint.latitude, lng: endPoint.longitude });
    }

    if (points.length === 0) {
      return 13; 
    }

    if (points.length === 1) {
      return isStarted ? 16 : 15;
    }

    const bounds = calculateBounds(points);
    if (!bounds) {
      return 13; 
    }
    const zoom = estimateZoom(bounds);

    return isStarted ? Math.max(zoom, 15) : Math.max(zoom, 13);
  }, [driverLocation, startPoint, endPoint, isStarted]);


  const mapMarkers = useMemo(() => {
    const markers: Array<{
      position: { lat: number; lng: number };
      title: string;
      color: string;
      icon?: google.maps.Icon;
    }> = [];


    if (startPoint.latitude && startPoint.longitude && !isNaN(startPoint.latitude) && !isNaN(startPoint.longitude)) {
      markers.push({
        position: { lat: startPoint.latitude, lng: startPoint.longitude },
        title: 'Pickup Location',
        color: Colors.success,
        icon: undefined, 
      });
    }


    if (endPoint.latitude && endPoint.longitude && !isNaN(endPoint.latitude) && !isNaN(endPoint.longitude)) {
      markers.push({
        position: { lat: endPoint.latitude, lng: endPoint.longitude },
        title: isStarted ? 'Destination' : 'Dropoff Location',
        color: Colors.error,
        icon: undefined, 
      });
    }


    if (driverLocation && driverLocation.lat && driverLocation.lng && !isNaN(driverLocation.lat) && !isNaN(driverLocation.lng)) {
      markers.push({
        position: { lat: driverLocation.lat, lng: driverLocation.lng },
        title: 'Your Location',
        color: Colors.primary,
        icon: undefined, 
      });
    }


    if (riderLocation && (riderLocation.latitude || riderLocation.lat) && (riderLocation.longitude || riderLocation.lng)) {
      const riderLat = riderLocation.latitude || riderLocation.lat;
      const riderLng = riderLocation.longitude || riderLocation.lng;
      if (!isNaN(riderLat) && !isNaN(riderLng)) {
        markers.push({
          position: { lat: riderLat, lng: riderLng },
          title: 'Rider Location',
          color: Colors.info,
          icon: undefined, 
        });
      }
    }

    return markers;
  }, [startPoint, endPoint, driverLocation, riderLocation, isStarted]);


  const polylines = useMemo(() => {
    const polylines: Array<{ path: Array<{ lat: number; lng: number }>; color: string; strokeWidth: number }> = [];

    if (isStarted) {

      if (driverLocation && driverLocation.lat && driverLocation.lng && 
          endPoint.latitude && endPoint.longitude && 
          !isNaN(driverLocation.lat) && !isNaN(driverLocation.lng) &&
          !isNaN(endPoint.latitude) && !isNaN(endPoint.longitude)) {

        if (routePath.length > 0) {

          const validPath = routePath
            .filter(p => p && typeof p.latitude === 'number' && typeof p.longitude === 'number' && !isNaN(p.latitude) && !isNaN(p.longitude))
            .map(p => ({ lat: p.latitude, lng: p.longitude }));

          if (validPath.length >= 2) {
            polylines.push({
              path: validPath,
              color: Colors.success,
              strokeWidth: 6,
            });
          } else {

            polylines.push({
              path: [{ lat: driverLocation.lat, lng: driverLocation.lng }, { lat: endPoint.latitude, lng: endPoint.longitude }],
              color: Colors.success,
              strokeWidth: 6,
            });
          }
        } else {

          polylines.push({
            path: [{ lat: driverLocation.lat, lng: driverLocation.lng }, { lat: endPoint.latitude, lng: endPoint.longitude }],
            color: Colors.success,
            strokeWidth: 6,
          });
        }
      }
    } else {

      if (startPoint.latitude && startPoint.longitude && endPoint.latitude && endPoint.longitude &&
          !isNaN(startPoint.latitude) && !isNaN(startPoint.longitude) && 
          !isNaN(endPoint.latitude) && !isNaN(endPoint.longitude)) {
        polylines.push({
          path: [{ lat: startPoint.latitude, lng: startPoint.longitude }, { lat: endPoint.latitude, lng: endPoint.longitude }],
          color: Colors.primary,
          strokeWidth: 4,
        });
      }
    }

    return polylines;
  }, [routePath, isStarted, driverLocation, startPoint, endPoint]);

  if (!currentRide) {
    return (
      <div className="active-ride-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }



  if (!mapCenter) {
    return (
      <div className="active-ride-container">
        <div className="active-ride-top-bar">
          <button className="icon-button" onClick={() => navigate('/')}>
            <MaterialCommunityIcons name="arrow-left" size={22} color={Colors.textPrimary} />
          </button>
          <div className="status-badge">
            <div className="status-dot" style={{ backgroundColor: getStatusColor(currentRide.status) }} />
            <span className="status-text">{normalizedStatus}</span>
          </div>
          <button className="icon-button" onClick={() => navigate(`/chat/${currentRide.id}`)}>
            <MaterialCommunityIcons name="message-text-outline" size={22} color={Colors.textPrimary} />
          </button>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 'calc(100vh - 80px)',
          flexDirection: 'column',
          gap: '16px',
          padding: '20px'
        }}>
          <MaterialCommunityIcons name="map-marker-off" size={64} color={Colors.textSecondary} />
          <p style={{ fontSize: '18px', fontWeight: 600, color: Colors.textSecondary, margin: 0 }}>
            Getting location...
          </p>
          <p style={{ fontSize: '14px', color: Colors.textSecondary, margin: 0, textAlign: 'center' }}>
            Please allow location access to see the map
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="active-ride-container">
      <div className={`map-container ${isMapExpanded ? 'expanded' : ''}`}>
        {isMapExpanded && (
          <button 
            className="close-map-button"
            onClick={() => setIsMapExpanded(false)}
            title="Close Map"
          >
            <MaterialCommunityIcons name="close" size={24} color={Colors.textPrimary} />
          </button>
        )}
        <MapView
          center={mapCenter || undefined}
          zoom={mapZoom}
          markers={mapMarkers}
          polylines={polylines}
        />
      </div>

      <div className="active-ride-top-bar">
        <button className="icon-button" onClick={() => navigate('/')}>
          <MaterialCommunityIcons name="arrow-left" size={22} color={Colors.textPrimary} />
        </button>
        <div className="status-badge">
          <div className="status-dot" style={{ backgroundColor: getStatusColor(currentRide.status) }} />
          <span className="status-text">{normalizedStatus}</span>
        </div>
        <button className="icon-button" onClick={() => navigate(`/chat/${currentRide.id}`)}>
          <MaterialCommunityIcons name="message-text-outline" size={22} color={Colors.textPrimary} />
        </button>
      </div>

      {isStarted && (
        <div className={`navigation-card ${isMapExpanded ? 'expanded' : ''}`}>
          <div className="navigation-header">
            <div className="navigation-icon-container">
              <MaterialCommunityIcons name="navigation-variant" size={30} color={Colors.white} />
            </div>
            <div className="navigation-info">
              <p className="navigation-title">Navigating to Destination</p>
              <p className="navigation-subtitle">
                {currentRide.destination_address || 
                 currentRide.locations?.[currentRide.locations?.length - 1]?.address ||
                 (endPoint.latitude && endPoint.longitude 
                 ? `${endPoint.latitude.toFixed(6)}, ${endPoint.longitude.toFixed(6)}`
                 : 'Destination')}
              </p>
            </div>
          </div>
          <div className="navigation-stats">
            <div className="stat-item">
              <div className="stat-icon-container">
                <MaterialCommunityIcons name="map-marker-distance" size={22} color={Colors.white} />
              </div>
              <p className="stat-value">{distance !== null && distance > 0 ? formatDistance(distance) : '--'}</p>
              <p className="stat-label">Distance</p>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <div className="stat-icon-container">
                <MaterialCommunityIcons name="clock-time-four-outline" size={22} color={Colors.white} />
              </div>
              <p className="stat-value">{eta !== null && eta > 0 ? formatETA(eta) : '--'}</p>
              <p className="stat-label">ETA</p>
            </div>
          </div>
          <button 
            className="view-map-button"
            onClick={() => setIsMapExpanded(!isMapExpanded)}
            title={isMapExpanded ? "Hide Map" : "View Full Map"}
          >
            <MaterialCommunityIcons 
              name={isMapExpanded ? "arrow-down" : "map"} 
              size={24} 
              color={Colors.white} 
            />
            <span>{isMapExpanded ? "Hide Map" : "View Map"}</span>
          </button>
        </div>
      )}

      <div className="active-ride-bottom-card">
        <div className="ride-header">
          <p className="ride-number">#{currentRide.ride_number}</p>
          <div className="status-container">
            <div className="status-indicator" style={{ backgroundColor: getStatusColor(currentRide.status) }} />
            <span className="status-label">{normalizedStatus}</span>
          </div>
        </div>

        {currentRide.rider && (
          <div className="rider-section">
            <div className="section-header">
              <MaterialCommunityIcons name="account-circle" size={22} color={Colors.white} />
              <span className="section-title">Rider Information</span>
            </div>
            <div className="rider-info-card">
              <div className="rider-avatar">
                <MaterialCommunityIcons name="account" size={28} color={Colors.white} />
              </div>
              <div className="rider-details">
                <p className="rider-name">{currentRide.rider.name}</p>
                <div className="rider-detail">
                  <MaterialCommunityIcons name="phone-outline" size={16} color={Colors.textSecondary} />
                  <span className="rider-phone">{currentRide.rider.phone}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="locations-section">
          <div className="location-card">
            <div className="location-icon pickup-icon">
              <MaterialCommunityIcons name="map-marker" size={26} color={Colors.white} />
            </div>
            <div className="location-text">
              <p className="location-label">PICKUP LOCATION</p>
              <p className="location-address">
                {currentRide.locations?.[0]?.address || currentRide.origin_address || 'Pickup location'}
              </p>
            </div>
          </div>

          <div className="connector-container">
            <div className="connector" />
          </div>

          <div className="location-card">
            <div className="location-icon dropoff-icon">
              <MaterialCommunityIcons name="map-marker" size={26} color={Colors.white} />
            </div>
            <div className="location-text">
              <p className="location-label">DROPOFF LOCATION</p>
              <p className="location-address">
                {currentRide.locations?.[currentRide.locations?.length - 1]?.address ||
                  currentRide.destination_address ||
                  'Dropoff location'}
              </p>
            </div>
          </div>
        </div>

        {currentRide.ride_fare && (
          <div className="fare-section">
            <div className="fare-icon-container">
              <MaterialCommunityIcons name="currency-usd" size={24} color={Colors.white} />
            </div>
            <div className="fare-info">
              <p className="fare-label">Ride Fare</p>
              <p className="fare-amount">
                {currentRide.currency_symbol || '$'}{currentRide.ride_fare}
              </p>
            </div>
          </div>
        )}

        <div className="action-buttons-container">
          {normalizedStatus === RideStatus.STARTED && (
            <button className="complete-button" onClick={handleComplete}>
              <MaterialCommunityIcons name="check-circle" size={26} color={Colors.white} />
              <span>Complete Ride</span>
            </button>
          )}

          <button className="chat-button" onClick={() => navigate(`/chat/${currentRide.id}`)}>
            <MaterialCommunityIcons name="message-text-outline" size={22} color={Colors.primary} />
            <span>Chat with Rider</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveRideScreen;

